import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Breadcrumbs as MUIBreadcrumbs, Typography, Box } from "@mui/material";
import { Icon } from "@iconify/react";
import { useContent } from "../../hooks/useContent";

const routeLabels = {
  "/home": "Home",
  "/email-support": "Email Support",
  "/support-tickets": "Support Tickets",
  "/support-tickets/create-ticket": "Create Ticket",
  "/support-tickets/edit-ticket": "Edit Ticket",
  "/create-organization": "Create Organization Account",
  "/manage-organizations": "Existing Organization Licenses",
  "/manage-organizations/update-organization": "Update Organization",
};

const Breadcrumbs = () => {
  const location = useLocation();
  const { pageHeading } = useContent();
  
  // Don't show breadcrumbs on home page
  if (location.pathname === "/home" || location.pathname === "/") {
    return null;
  }

  const pathnames = location.pathname.split("/").filter((x) => x);
  
  // Build breadcrumb items
  const breadcrumbItems = [
    { path: "/home", label: "Home" }
  ];

  let currentPath = "";
  pathnames.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    // Skip dynamic segments (like IDs) unless they're the last segment
    if (!isNaN(segment) || /^[0-9a-f-]{36}$/i.test(segment)) {
      return;
    }
    
    const label = routeLabels[currentPath] || segment
      .split("-")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    
    breadcrumbItems.push({ path: currentPath, label });
  });

  return (
    <Box
      className="breadcrumb-container"
      sx={{
        marginTop: "12px",
        transition: "all 0.3s ease",
      }}
    >
      <MUIBreadcrumbs
        separator={
          <Icon
            icon="mdi:chevron-right"
            style={{ color: "var(--dark-text-muted)", fontSize: "20px" }}
          />
        }
        aria-label="breadcrumb"
        sx={{
          "& .MuiBreadcrumbs-ol": {
            flexWrap: "nowrap",
            justifyContent: "center",
          },
        }}
      >
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          
          if (isLast) {
            return (
              <Box
                key={item.path}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Icon
                  icon="mdi:circle"
                  style={{
                    fontSize: "8px",
                    color: "var(--primary-color)",
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "var(--text-color)",
                    fontSize: "14px",
                    letterSpacing: "0.2px",
                  }}
                >
                  {pageHeading || item.label}
                </Typography>
              </Box>
            );
          }

          return (
            <Link
              key={item.path}
              to={item.path}
              style={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateX(2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateX(0)";
              }}
            >
              {index === 0 && (
                <Icon
                  icon="mdi:home"
                  style={{
                    fontSize: "18px",
                    color: "var(--dark-text-muted)",
                  }}
                />
              )}
              <Typography
                sx={{
                  color: "var(--dark-text-muted)",
                  fontSize: "14px",
                  fontWeight: 500,
                  "&:hover": {
                    color: "var(--primary-color)",
                  },
                  transition: "color 0.2s ease",
                }}
              >
                {item.label}
              </Typography>
            </Link>
          );
        })}
      </MUIBreadcrumbs>
    </Box>
  );
};

export default Breadcrumbs;

